<template>
  <AdminLayout>
    <div class="p-6 lg:p-8 max-w-4xl">
      <!-- 페이지 헤더 -->
      <div class="mb-8">
        <router-link
          to="/admin/properties"
          class="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-primary mb-3 transition"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
          현장 목록으로 돌아가기
        </router-link>
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
            <svg class="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div>
            <h1 class="text-2xl font-bold text-slate-800">새 현장 등록</h1>
            <p class="text-sm text-slate-500">분양 현장 정보를 입력하세요</p>
          </div>
        </div>
      </div>

      <!-- 진행 상태 표시 -->
      <div class="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 mb-6">
        <div class="flex items-center justify-between">
          <div
            v-for="(section, index) in sectionList"
            :key="section.key"
            class="flex items-center"
          >
            <div class="flex items-center gap-2">
              <div
                :class="[
                  'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all',
                  openSections[section.key]
                    ? 'bg-primary text-white'
                    : 'bg-slate-100 text-slate-500'
                ]"
              >
                {{ index + 1 }}
              </div>
              <span
                :class="[
                  'text-sm font-medium hidden sm:inline',
                  openSections[section.key] ? 'text-primary' : 'text-slate-500'
                ]"
              >
                {{ section.label }}
              </span>
            </div>
            <div
              v-if="index < sectionList.length - 1"
              class="w-8 sm:w-12 h-0.5 bg-slate-200 mx-2"
            ></div>
          </div>
        </div>
      </div>

      <form @submit.prevent="handleSubmit">
        <!-- 섹션 1: 기본 정보 -->
        <section class="bg-white rounded-2xl shadow-sm border border-slate-100 mb-5 overflow-hidden">
          <button
            type="button"
            @click="toggleSection('basic')"
            class="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition"
          >
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <svg class="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <span class="font-semibold text-slate-800">기본 정보</span>
              <span class="text-xs text-red-500 font-medium">필수</span>
            </div>
            <svg
              class="w-5 h-5 text-slate-400 transition-transform duration-200"
              :class="{ 'rotate-180': openSections.basic }"
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>

          <div v-show="openSections.basic" class="p-6 border-t border-slate-100 space-y-5">
            <!-- 현장명 -->
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-2">
                현장명 <span class="text-red-500">*</span>
              </label>
              <input
                v-model="form.name"
                type="text"
                placeholder="예: 래미안 강남 포레스트"
                class="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition"
                required
              />
            </div>

            <!-- 주소 -->
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-2">
                주소 <span class="text-red-500">*</span>
              </label>
              <div class="flex gap-2">
                <input
                  v-model="form.address"
                  type="text"
                  placeholder="예: 서울시 강남구 역삼동 123-45"
                  class="flex-1 px-4 py-3 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition"
                  required
                />
                <button
                  type="button"
                  class="px-5 py-3 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 transition whitespace-nowrap font-medium"
                  @click="searchAddress"
                >
                  <svg class="w-5 h-5 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                  </svg>
                  검색
                </button>
              </div>
              <!-- 지도 미리보기 -->
              <div v-if="form.coordinates" class="mt-3">
                <div ref="mapPreviewContainer" class="w-full h-40 rounded-xl overflow-hidden bg-slate-100"></div>
              </div>
            </div>

            <!-- 지역 선택 -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">
                  광역시/도 <span class="text-red-500">*</span>
                </label>
                <select
                  v-model="form.regionMain"
                  class="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition appearance-none bg-white"
                  required
                >
                  <option value="">선택하세요</option>
                  <option v-for="region in regionMainOptions" :key="region" :value="region">
                    {{ region }}
                  </option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">시/군/구</label>
                <input
                  v-model="form.regionSub"
                  type="text"
                  placeholder="예: 강남구, 수원시"
                  class="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition"
                />
              </div>
            </div>

            <!-- 부동산 타입 -->
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-2">
                부동산 타입 <span class="text-red-500">*</span>
              </label>
              <div class="grid grid-cols-4 gap-3">
                <label
                  v-for="typeOption in propertyTypeOptions"
                  :key="typeOption.value"
                  :class="[
                    'flex items-center justify-center gap-2 p-3 rounded-xl border-2 cursor-pointer transition-all',
                    form.propertyType === typeOption.value
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-slate-200 hover:border-slate-300'
                  ]"
                >
                  <input
                    type="radio"
                    v-model="form.propertyType"
                    :value="typeOption.value"
                    class="hidden"
                  />
                  <span class="text-lg">{{ typeOption.icon }}</span>
                  <span class="font-medium text-sm">{{ typeOption.label }}</span>
                </label>
              </div>
            </div>

            <!-- 초기 자금 정보 -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">초기 자금 (계약금)</label>
                <input
                  v-model="form.initialFund"
                  type="text"
                  placeholder="예: 1,000만원, 500만원"
                  class="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition"
                />
                <p class="text-xs text-slate-400 mt-1">사용자에게 표시될 텍스트</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">중도금 조건</label>
                <input
                  v-model="form.middlePayment"
                  type="text"
                  placeholder="예: 무이자, 60% 중도금 대출"
                  class="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition"
                />
              </div>
            </div>

            <!-- 테마 태그 -->
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-2">테마 태그 (복수 선택 가능)</label>
              <div class="flex flex-wrap gap-2">
                <label
                  v-for="theme in themeOptions"
                  :key="theme.id"
                  :class="[
                    'flex items-center gap-2 px-4 py-2 rounded-full border-2 cursor-pointer transition-all',
                    form.themes.includes(theme.id)
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-slate-200 hover:border-slate-300'
                  ]"
                >
                  <input
                    type="checkbox"
                    :value="theme.id"
                    v-model="form.themes"
                    class="hidden"
                  />
                  <span>{{ theme.emoji }}</span>
                  <span class="font-medium text-sm">{{ theme.label }}</span>
                </label>
              </div>
              <p class="text-xs text-slate-400 mt-2">챗봇 추천 및 테마별 검색에 사용됩니다</p>
            </div>

            <!-- 대표 이미지 -->
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-2">대표 이미지</label>
              <div
                class="relative border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-primary/50 hover:bg-primary/5 transition-all cursor-pointer group"
                @click="$refs.thumbnailInput.click()"
                @dragover.prevent
                @drop.prevent="handleThumbnailDrop"
              >
                <input
                  ref="thumbnailInput"
                  type="file"
                  accept="image/*"
                  class="hidden"
                  @change="handleThumbnailChange"
                />
                <div v-if="thumbnailPreview" class="relative">
                  <img :src="thumbnailPreview" class="max-h-48 mx-auto rounded-xl shadow-lg" />
                  <button
                    type="button"
                    @click.stop="removeThumbnail"
                    class="absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition shadow-lg"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
                <div v-else class="text-slate-400">
                  <div class="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-primary/10 transition">
                    <svg class="w-8 h-8 text-slate-400 group-hover:text-primary transition" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                  </div>
                  <p class="font-medium text-slate-600">클릭하거나 이미지를 드래그하세요</p>
                  <p class="text-xs mt-1">자동으로 WebP로 변환 및 압축됩니다</p>
                </div>
              </div>
            </div>

            <!-- 분양 상태 -->
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-2">분양 상태</label>
              <div class="grid grid-cols-3 gap-3">
                <label
                  v-for="status in statusOptions"
                  :key="status.value"
                  :class="[
                    'flex items-center justify-center gap-2 p-3 rounded-xl border-2 cursor-pointer transition-all',
                    form.status === status.value
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-slate-200 hover:border-slate-300'
                  ]"
                >
                  <input
                    type="radio"
                    v-model="form.status"
                    :value="status.value"
                    class="hidden"
                  />
                  <span :class="['w-2 h-2 rounded-full', status.color]"></span>
                  <span class="font-medium text-sm">{{ status.label }}</span>
                </label>
              </div>
            </div>
          </div>
        </section>

        <!-- 섹션 2: 건물 정보 -->
        <section class="bg-white rounded-2xl shadow-sm border border-slate-100 mb-5 overflow-hidden">
          <button
            type="button"
            @click="toggleSection('building')"
            class="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition"
          >
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                <svg class="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                </svg>
              </div>
              <span class="font-semibold text-slate-800">건물 정보</span>
            </div>
            <svg
              class="w-5 h-5 text-slate-400 transition-transform duration-200"
              :class="{ 'rotate-180': openSections.building }"
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>

          <div v-show="openSections.building" class="p-6 border-t border-slate-100">
            <div class="grid grid-cols-2 gap-4">
              <!-- 세대수 -->
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">세대수</label>
                <div class="relative">
                  <input
                    v-model.number="form.totalUnits"
                    type="number"
                    placeholder="1500"
                    class="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition"
                  />
                  <span class="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">세대</span>
                </div>
              </div>

              <!-- 동 개수 -->
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">동 개수</label>
                <div class="relative">
                  <input
                    v-model.number="form.totalBuildings"
                    type="number"
                    placeholder="15"
                    class="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition"
                  />
                  <span class="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">동</span>
                </div>
              </div>

              <!-- 최고층 -->
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">최고층</label>
                <div class="relative">
                  <input
                    v-model.number="form.maxFloor"
                    type="number"
                    placeholder="35"
                    class="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition"
                  />
                  <span class="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">층</span>
                </div>
              </div>

              <!-- 주차 (세대당) -->
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">주차 (세대당)</label>
                <div class="relative">
                  <input
                    v-model.number="form.parkingPerUnit"
                    type="number"
                    step="0.1"
                    placeholder="1.5"
                    class="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition"
                  />
                  <span class="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">대</span>
                </div>
              </div>

              <!-- 용적률 -->
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">용적률</label>
                <div class="relative">
                  <input
                    v-model.number="form.floorAreaRatio"
                    type="number"
                    placeholder="299"
                    class="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition"
                  />
                  <span class="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                </div>
              </div>

              <!-- 건폐율 -->
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">건폐율</label>
                <div class="relative">
                  <input
                    v-model.number="form.buildingCoverageRatio"
                    type="number"
                    placeholder="18"
                    class="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition"
                  />
                  <span class="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                </div>
              </div>

              <!-- 가스 방식 -->
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">가스 방식</label>
                <select
                  v-model="form.gasType"
                  class="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition appearance-none bg-white"
                >
                  <option value="">선택하세요</option>
                  <option value="city">도시가스</option>
                  <option value="individual">개별난방</option>
                  <option value="lpg">LPG</option>
                </select>
              </div>

              <!-- 난방 형식 -->
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">난방 형식</label>
                <select
                  v-model="form.heatingType"
                  class="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition appearance-none bg-white"
                >
                  <option value="">선택하세요</option>
                  <option value="individual">개별난방</option>
                  <option value="central">중앙난방</option>
                  <option value="district">지역난방</option>
                </select>
              </div>

              <!-- 입주 예정일 -->
              <div class="col-span-2">
                <label class="block text-sm font-medium text-slate-700 mb-2">입주 예정일</label>
                <input
                  v-model="form.moveInDate"
                  type="month"
                  class="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition"
                />
              </div>
            </div>
          </div>
        </section>

        <!-- 섹션 3: 평형/타입 -->
        <section class="bg-white rounded-2xl shadow-sm border border-slate-100 mb-5 overflow-hidden">
          <button
            type="button"
            @click="toggleSection('types')"
            class="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition"
          >
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                <svg class="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"/>
                </svg>
              </div>
              <span class="font-semibold text-slate-800">평형/타입 관리</span>
              <span class="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                {{ form.types.length }}개
              </span>
            </div>
            <svg
              class="w-5 h-5 text-slate-400 transition-transform duration-200"
              :class="{ 'rotate-180': openSections.types }"
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>

          <div v-show="openSections.types" class="p-6 border-t border-slate-100">
            <!-- 타입 목록 -->
            <div class="space-y-4 mb-4">
              <div
                v-for="(type, index) in form.types"
                :key="index"
                class="border border-slate-200 rounded-xl p-5 bg-slate-50/50 hover:bg-white transition"
              >
                <div class="flex items-center justify-between mb-4">
                  <span class="font-semibold text-slate-700 flex items-center gap-2">
                    <span class="w-6 h-6 bg-violet-100 text-violet-600 rounded-lg flex items-center justify-center text-xs font-bold">
                      {{ index + 1 }}
                    </span>
                    타입 {{ index + 1 }}
                  </span>
                  <button
                    type="button"
                    @click="removeType(index)"
                    class="text-red-500 hover:text-red-700 text-sm font-medium flex items-center gap-1"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                    삭제
                  </button>
                </div>

                <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <!-- 타입명 -->
                  <div>
                    <label class="block text-xs font-medium text-slate-500 mb-1">타입명</label>
                    <input
                      v-model="type.name"
                      type="text"
                      placeholder="84A"
                      class="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition"
                    />
                  </div>

                  <!-- 공급면적 -->
                  <div>
                    <label class="block text-xs font-medium text-slate-500 mb-1">공급면적 (m2)</label>
                    <input
                      v-model.number="type.supplyArea"
                      type="number"
                      placeholder="84"
                      class="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition"
                    />
                  </div>

                  <!-- 평수 -->
                  <div>
                    <label class="block text-xs font-medium text-slate-500 mb-1">평수</label>
                    <input
                      v-model.number="type.pyeong"
                      type="number"
                      placeholder="34"
                      class="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition"
                    />
                  </div>

                  <!-- 분양가 -->
                  <div>
                    <label class="block text-xs font-medium text-slate-500 mb-1">분양가 (만원)</label>
                    <input
                      v-model.number="type.price"
                      type="number"
                      placeholder="120000"
                      class="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition"
                    />
                  </div>
                </div>

                <!-- 해당 동 -->
                <div class="mt-3">
                  <label class="block text-xs font-medium text-slate-500 mb-1">해당 동 (쉼표로 구분)</label>
                  <input
                    v-model="type.buildingsText"
                    type="text"
                    placeholder="101, 102, 103"
                    class="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition"
                  />
                </div>

                <!-- 타입 이미지 -->
                <div class="mt-3">
                  <label class="block text-xs font-medium text-slate-500 mb-1">평면도 이미지</label>
                  <div
                    class="border border-dashed border-slate-300 rounded-xl p-4 text-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition"
                    @click="triggerTypeImageUpload(index)"
                  >
                    <input
                      :ref="el => typeImageInputs[index] = el"
                      type="file"
                      accept="image/*"
                      class="hidden"
                      @change="e => handleTypeImageChange(index, e)"
                    />
                    <div v-if="type.imagePreview" class="relative inline-block">
                      <img :src="type.imagePreview" class="max-h-24 mx-auto rounded-lg" />
                    </div>
                    <div v-else class="text-slate-400 text-sm flex items-center justify-center gap-2">
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                      </svg>
                      평면도 이미지 추가
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 타입 추가 버튼 -->
            <button
              type="button"
              @click="addType"
              class="w-full py-4 border-2 border-dashed border-slate-200 rounded-xl text-slate-500 hover:border-primary hover:text-primary hover:bg-primary/5 transition flex items-center justify-center gap-2 font-medium"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
              </svg>
              새 타입 추가
            </button>
          </div>
        </section>

        <!-- 섹션 4: 주변 시설 -->
        <section class="bg-white rounded-2xl shadow-sm border border-slate-100 mb-6 overflow-hidden">
          <button
            type="button"
            @click="toggleSection('facilities')"
            class="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition"
          >
            <div class="flex items-center gap-3">
              <div class="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                <svg class="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
              </div>
              <span class="font-semibold text-slate-800">주변 시설</span>
              <span class="text-xs text-slate-400">선택사항</span>
            </div>
            <svg
              class="w-5 h-5 text-slate-400 transition-transform duration-200"
              :class="{ 'rotate-180': openSections.facilities }"
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>

          <div v-show="openSections.facilities" class="p-6 border-t border-slate-100">
            <!-- 자동 검색 버튼 -->
            <div class="mb-6">
              <button
                type="button"
                @click="searchNearbyFacilities"
                :disabled="isSearchingFacilities || !form.coordinates"
                class="w-full flex items-center justify-center gap-2 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition disabled:bg-slate-300 disabled:cursor-not-allowed"
              >
                <svg v-if="isSearchingFacilities" class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
                <span v-if="isSearchingFacilities">검색 중...</span>
                <span v-else>주변 시설 자동 검색</span>
              </button>
              <p v-if="!form.coordinates" class="text-xs text-slate-400 mt-2 text-center">
                먼저 주소를 검색해주세요
              </p>
              <p v-if="facilitySearchError" class="text-xs text-red-500 mt-2 text-center">
                {{ facilitySearchError }}
              </p>
            </div>

            <!-- 주변 편의시설 (이름 + 거리) -->
            <div class="mb-6">
              <h4 class="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <span class="w-2 h-2 bg-emerald-500 rounded-full"></span>
                주변 편의시설 (3km 이내)
              </h4>
              <div class="space-y-4">
                <div v-for="(facilities, key) in form.facilities.convenience" :key="key">
                  <label class="block text-xs font-medium text-slate-500 mb-2">{{ facilityLabels.convenience[key] }}</label>
                  <div v-if="facilities.length > 0" class="flex flex-wrap gap-2 mb-2">
                    <span
                      v-for="(facility, index) in facilities"
                      :key="index"
                      class="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-sm"
                    >
                      {{ facility.name }} <span class="text-emerald-500 text-xs">({{ formatDistance(facility.distance) }})</span>
                      <button
                        type="button"
                        @click="removeFacility('convenience', key, index)"
                        class="ml-1 hover:text-emerald-900"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>
                    </span>
                  </div>
                  <p v-else class="text-sm text-slate-400 mb-2">검색된 시설이 없습니다</p>
                  <!-- 수동 추가 -->
                  <div class="flex gap-2">
                    <input
                      v-model="manualInput.convenience[key].name"
                      type="text"
                      placeholder="시설명"
                      class="flex-1 px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/20 outline-none"
                    />
                    <input
                      v-model="manualInput.convenience[key].distance"
                      type="number"
                      placeholder="거리(m)"
                      class="w-24 px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/20 outline-none"
                    />
                    <button
                      type="button"
                      @click="addFacilityManual('convenience', key)"
                      class="px-3 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition"
                    >
                      추가
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 주변 대중교통 -->
            <div class="mb-6">
              <h4 class="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <span class="w-2 h-2 bg-blue-500 rounded-full"></span>
                주변 대중교통 (3km 이내)
              </h4>
              <div class="space-y-4">
                <div v-for="(facilities, key) in form.facilities.transport" :key="key">
                  <label class="block text-xs font-medium text-slate-500 mb-2">{{ facilityLabels.transport[key] }}</label>
                  <div v-if="facilities.length > 0" class="flex flex-wrap gap-2 mb-2">
                    <span
                      v-for="(facility, index) in facilities"
                      :key="index"
                      class="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm"
                    >
                      {{ facility.name }} <span class="text-blue-500 text-xs">({{ formatDistance(facility.distance) }})</span>
                      <button
                        type="button"
                        @click="removeFacility('transport', key, index)"
                        class="ml-1 hover:text-blue-900"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>
                    </span>
                  </div>
                  <p v-else class="text-sm text-slate-400 mb-2">검색된 시설이 없습니다</p>
                  <!-- 수동 추가 -->
                  <div class="flex gap-2">
                    <input
                      v-model="manualInput.transport[key].name"
                      type="text"
                      placeholder="시설명"
                      class="flex-1 px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 outline-none"
                    />
                    <input
                      v-model="manualInput.transport[key].distance"
                      type="number"
                      placeholder="거리(m)"
                      class="w-24 px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 outline-none"
                    />
                    <button
                      type="button"
                      @click="addFacilityManual('transport', key)"
                      class="px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition"
                    >
                      추가
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 주변 학군 -->
            <div>
              <h4 class="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <span class="w-2 h-2 bg-violet-500 rounded-full"></span>
                주변 학군 (3km 이내)
              </h4>
              <div class="space-y-4">
                <div v-for="(facilities, key) in form.facilities.education" :key="key">
                  <label class="block text-xs font-medium text-slate-500 mb-2">{{ facilityLabels.education[key] }}</label>
                  <div v-if="facilities.length > 0" class="flex flex-wrap gap-2 mb-2">
                    <span
                      v-for="(facility, index) in facilities"
                      :key="index"
                      class="inline-flex items-center gap-1 px-3 py-1.5 bg-violet-50 text-violet-700 rounded-lg text-sm"
                    >
                      {{ facility.name }} <span class="text-violet-500 text-xs">({{ formatDistance(facility.distance) }})</span>
                      <button
                        type="button"
                        @click="removeFacility('education', key, index)"
                        class="ml-1 hover:text-violet-900"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>
                    </span>
                  </div>
                  <p v-else class="text-sm text-slate-400 mb-2">검색된 시설이 없습니다</p>
                  <!-- 수동 추가 -->
                  <div class="flex gap-2">
                    <input
                      v-model="manualInput.education[key].name"
                      type="text"
                      placeholder="시설명"
                      class="flex-1 px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20 outline-none"
                    />
                    <input
                      v-model="manualInput.education[key].distance"
                      type="number"
                      placeholder="거리(m)"
                      class="w-24 px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-violet-500 focus:ring-1 focus:ring-violet-500/20 outline-none"
                    />
                    <button
                      type="button"
                      @click="addFacilityManual('education', key)"
                      class="px-3 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 transition"
                    >
                      추가
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- 에러 메시지 -->
        <div v-if="errorMessage" class="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-xl flex items-center gap-3">
          <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
          {{ errorMessage }}
        </div>

        <!-- 제출 버튼 -->
        <div class="flex gap-4">
          <button
            type="button"
            @click="$router.push('/admin/properties')"
            class="flex-1 py-3.5 border border-slate-200 text-slate-700 font-medium rounded-xl hover:bg-slate-50 transition"
          >
            취소
          </button>
          <button
            type="submit"
            :disabled="isSubmitting"
            class="flex-1 py-3.5 bg-primary text-white font-medium rounded-xl hover:bg-primary-dark shadow-lg shadow-primary/25 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <svg v-if="isSubmitting" class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span v-if="isSubmitting">등록 중...</span>
            <span v-else>현장 등록하기</span>
          </button>
        </div>
      </form>
    </div>

  </AdminLayout>
</template>

<script>
import { ref, reactive, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'
import { useProperty } from '@/composables/admin/useProperty'

export default {
  name: 'PropertyCreateView',
  components: {
    AdminLayout
  },
  setup() {
    const router = useRouter()
    const { createProperty } = useProperty()

    const isSubmitting = ref(false)
    const errorMessage = ref('')
    const thumbnailFile = ref(null)
    const thumbnailPreview = ref('')
    const typeImageInputs = ref({})
    const typeImageFiles = ref({})
    const mapPreviewContainer = ref(null)
    const mapPreview = ref(null)


    // 섹션 열림 상태
    const openSections = reactive({
      basic: true,
      building: true,
      types: true,
      facilities: false
    })

    const sectionList = [
      { key: 'basic', label: '기본 정보' },
      { key: 'building', label: '건물 정보' },
      { key: 'types', label: '평형/타입' },
      { key: 'facilities', label: '주변 시설' }
    ]

    const statusOptions = [
      { value: 'upcoming', label: '분양 예정', color: 'bg-amber-500' },
      { value: 'ongoing', label: '분양 중', color: 'bg-emerald-500' },
      { value: 'completed', label: '분양 완료', color: 'bg-slate-400' }
    ]

    // 지역 옵션
    const regionMainOptions = [
      '서울', '경기', '인천', '충북', '충남', '강원',
      '대전', '세종', '경북', '경남', '대구', '부산',
      '울산', '전북', '전남', '광주', '제주'
    ]

    // 부동산 타입 옵션
    const propertyTypeOptions = [
      { value: 'apartment', label: '아파트', icon: '🏢' },
      { value: 'officetel', label: '오피스텔', icon: '🏬' },
      { value: 'villa', label: '빌라', icon: '🏠' },
      { value: 'commercial', label: '상가', icon: '🏪' }
    ]

    // 테마 옵션 (챗봇/카테고리 연동)
    const themeOptions = [
      { id: 'money', label: '실투자 3천만', emoji: '💰' },
      { id: 'brand', label: '1군 브랜드', emoji: '🏢' },
      { id: 'subway', label: '초역세권', emoji: '🚄' },
      { id: 'safe', label: '안심 보장제', emoji: '🛡️' },
      { id: 'school', label: '명문 학세권', emoji: '🎓' },
      { id: 'forest', label: '힐링 숲세권', emoji: '🌳' },
      { id: 'job', label: '직주근접', emoji: '💼' },
      { id: 'mall', label: '몰세권', emoji: '🛍️' }
    ]

    // 시설 라벨 (단순화)
    const facilityLabels = {
      // 주변 편의시설
      convenience: {
        mart: '마트',
        department: '백화점',
        hospital: '종합병원',
        park: '공원',
        pharmacy: '약국'
      },
      // 주변 대중교통
      transport: {
        subway: '지하철역',
        rail: '고속철도/일반철도',
        bus: '버스정류장'
      },
      // 주변 학군
      education: {
        daycare: '어린이집',
        kindergarten: '유치원',
        elementary: '초등학교',
        middle: '중학교',
        high: '고등학교'
      }
    }

    // 폼 데이터
    const form = reactive({
      name: '',
      address: '',
      regionMain: '',
      regionSub: '',
      propertyType: 'apartment',
      initialFund: '',
      middlePayment: '',
      themes: [],
      status: 'ongoing',
      totalUnits: null,
      totalBuildings: null,
      maxFloor: null,
      parkingPerUnit: null,
      floorAreaRatio: null,
      buildingCoverageRatio: null,
      gasType: '',
      heatingType: '',
      moveInDate: '',
      types: [],
      facilities: {
        // 주변 편의시설 (이름 + 거리)
        convenience: {
          mart: [],           // 마트 (MT1 + 키워드)
          department: [],     // 백화점 (키워드)
          hospital: [],       // 종합병원 (HP8)
          park: [],           // 공원 (키워드)
          pharmacy: []        // 약국 (PM9)
        },
        // 주변 대중교통 (이름 + 거리)
        transport: {
          subway: [],         // 지하철역 (SW8)
          rail: [],           // 고속철도/일반철도 (KTX, SRT, ITX, 무궁화, 새마을)
          bus: []             // 버스정류장
        },
        // 주변 학군 (이름 + 거리)
        education: {
          daycare: [],        // 어린이집 (PS3)
          kindergarten: [],   // 유치원
          elementary: [],     // 초등학교
          middle: [],         // 중학교
          high: []            // 고등학교
        }
      },
      coordinates: null // { lat, lng }
    })

    // 수동 입력용 상태
    const manualInput = reactive({
      convenience: {
        mart: { name: '', distance: '' },
        department: { name: '', distance: '' },
        hospital: { name: '', distance: '' },
        park: { name: '', distance: '' },
        pharmacy: { name: '', distance: '' }
      },
      transport: {
        subway: { name: '', distance: '' },
        rail: { name: '', distance: '' },
        bus: { name: '', distance: '' }
      },
      education: {
        daycare: { name: '', distance: '' },
        kindergarten: { name: '', distance: '' },
        elementary: { name: '', distance: '' },
        middle: { name: '', distance: '' },
        high: { name: '', distance: '' }
      }
    })

    const toggleSection = (section) => {
      openSections[section] = !openSections[section]
    }

    // 대표 이미지 처리
    const handleThumbnailChange = (event) => {
      const file = event.target.files[0]
      if (file) {
        thumbnailFile.value = file
        thumbnailPreview.value = URL.createObjectURL(file)
      }
    }

    const handleThumbnailDrop = (event) => {
      const file = event.dataTransfer.files[0]
      if (file && file.type.startsWith('image/')) {
        thumbnailFile.value = file
        thumbnailPreview.value = URL.createObjectURL(file)
      }
    }

    const removeThumbnail = () => {
      thumbnailFile.value = null
      thumbnailPreview.value = ''
    }

    // 타입 관리
    const addType = () => {
      form.types.push({
        name: '',
        supplyArea: null,
        pyeong: null,
        price: null,
        buildingsText: '',
        imageUrl: '',
        imagePreview: ''
      })
    }

    const removeType = (index) => {
      form.types.splice(index, 1)
      delete typeImageFiles.value[index]
    }

    const triggerTypeImageUpload = (index) => {
      if (typeImageInputs.value[index]) {
        typeImageInputs.value[index].click()
      }
    }

    const handleTypeImageChange = (index, event) => {
      const file = event.target.files[0]
      if (file) {
        typeImageFiles.value[index] = file
        form.types[index].imagePreview = URL.createObjectURL(file)
      }
    }

    // 다음 우편번호 서비스로 주소 검색
    const searchAddress = () => {
      new window.daum.Postcode({
        oncomplete: (data) => {
          // 도로명 주소 또는 지번 주소
          const address = data.roadAddress || data.jibunAddress

          form.address = address

          // 지역 자동 설정
          form.regionMain = data.sido || ''
          form.regionSub = data.sigungu || ''

          // 카카오맵으로 좌표 검색
          getCoordinates(address)
        }
      }).open()
    }

    // 카카오맵 SDK 로드 보장
    const ensureKakaoMapsLoaded = () => {
      return new Promise((resolve, reject) => {
        // 디버깅: window.kakao 상태 확인
        console.log('window.kakao:', window.kakao)
        console.log('window.kakao.maps:', window.kakao?.maps)

        // 카카오 SDK 자체가 없는 경우
        if (!window.kakao) {
          reject(new Error('카카오 SDK가 로드되지 않았습니다. 네트워크를 확인해주세요.'))
          return
        }

        // kakao.maps 객체가 없는 경우 - SDK 스크립트 로드 실패
        if (!window.kakao.maps) {
          reject(new Error('카카오맵 SDK가 로드되지 않았습니다. 카카오 개발자 콘솔에서 도메인(localhost)이 등록되어 있는지 확인해주세요.'))
          return
        }

        // services가 이미 있으면 로드 완료된 상태
        if (window.kakao.maps.services) {
          resolve()
          return
        }

        // autoload=false이므로 load() 호출하여 SDK 완전 로드
        if (typeof window.kakao.maps.load === 'function') {
          window.kakao.maps.load(() => {
            console.log('카카오맵 SDK 로드 완료')
            resolve()
          })
        } else {
          reject(new Error('카카오맵 SDK load 함수를 찾을 수 없습니다'))
        }
      })
    }

    // 주소로 좌표 검색
    const getCoordinates = async (address) => {
      try {
        await ensureKakaoMapsLoaded()

        const geocoder = new window.kakao.maps.services.Geocoder()
        geocoder.addressSearch(address, (result, status) => {
          if (status === window.kakao.maps.services.Status.OK) {
            form.coordinates = {
              lat: parseFloat(result[0].y),
              lng: parseFloat(result[0].x)
            }
            nextTick(() => {
              initMapPreview()
            })
          }
        })
      } catch (error) {
        console.error(error.message)
      }
    }

    // 지도 미리보기 초기화
    const initMapPreview = async () => {
      if (!mapPreviewContainer.value || !form.coordinates) return

      try {
        await ensureKakaoMapsLoaded()

        const coords = new window.kakao.maps.LatLng(form.coordinates.lat, form.coordinates.lng)

        const options = {
          center: coords,
          level: 3
        }
        mapPreview.value = new window.kakao.maps.Map(mapPreviewContainer.value, options)

        // 마커 추가
        new window.kakao.maps.Marker({
          map: mapPreview.value,
          position: coords
        })
      } catch (error) {
        console.error('지도 초기화 오류:', error.message)
      }
    }

    // 주변 시설 자동 검색
    const isSearchingFacilities = ref(false)
    const facilitySearchError = ref('')

    // 두 좌표 사이 거리 계산 (미터)
    const calculateDistance = (lat1, lng1, lat2, lng2) => {
      const R = 6371000 // 지구 반경 (미터)
      const dLat = (lat2 - lat1) * Math.PI / 180
      const dLng = (lng2 - lng1) * Math.PI / 180
      const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
                Math.sin(dLng/2) * Math.sin(dLng/2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
      return Math.round(R * c)
    }

    // 거리 포맷팅
    const formatDistance = (meters) => {
      if (meters < 1000) return `${meters}m`
      return `${(meters / 1000).toFixed(1)}km`
    }

    // 키워드로 장소 검색 (페이지네이션으로 모든 결과 가져오기)
    const searchByKeyword = (keyword, targetKey, category, radius = 3000) => {
      return new Promise((resolve) => {
        const places = new window.kakao.maps.services.Places()
        const coords = form.coordinates
        const allResults = []

        const callback = (result, status, pagination) => {
          if (status === window.kakao.maps.services.Status.OK) {
            allResults.push(...result)
            // 다음 페이지가 있으면 계속 가져옴 (최대 45개, 카카오 API 제한)
            if (pagination.hasNextPage) {
              pagination.nextPage()
            } else {
              const facilities = allResults.map(place => ({
                name: place.place_name,
                distance: calculateDistance(
                  coords.lat, coords.lng,
                  parseFloat(place.y), parseFloat(place.x)
                )
              }))
              facilities.sort((a, b) => a.distance - b.distance)
              resolve({ key: targetKey, category, facilities })
            }
          } else {
            // 결과가 있으면 반환, 없으면 빈 배열
            if (allResults.length > 0) {
              const facilities = allResults.map(place => ({
                name: place.place_name,
                distance: calculateDistance(
                  coords.lat, coords.lng,
                  parseFloat(place.y), parseFloat(place.x)
                )
              }))
              facilities.sort((a, b) => a.distance - b.distance)
              resolve({ key: targetKey, category, facilities })
            } else {
              resolve({ key: targetKey, category, facilities: [] })
            }
          }
        }

        places.keywordSearch(keyword, callback, {
          location: new window.kakao.maps.LatLng(coords.lat, coords.lng),
          radius,
          sort: window.kakao.maps.services.SortBy.DISTANCE
        })
      })
    }

    // 카테고리로 장소 검색 (페이지네이션으로 모든 결과 가져오기)
    const searchByCategory = (categoryCode, targetKey, category, radius = 3000) => {
      return new Promise((resolve) => {
        const places = new window.kakao.maps.services.Places()
        const coords = form.coordinates
        const allResults = []

        const callback = (result, status, pagination) => {
          if (status === window.kakao.maps.services.Status.OK) {
            allResults.push(...result)
            // 다음 페이지가 있으면 계속 가져옴 (최대 45개, 카카오 API 제한)
            if (pagination.hasNextPage) {
              pagination.nextPage()
            } else {
              const facilities = allResults.map(place => ({
                name: place.place_name,
                distance: calculateDistance(
                  coords.lat, coords.lng,
                  parseFloat(place.y), parseFloat(place.x)
                )
              }))
              facilities.sort((a, b) => a.distance - b.distance)
              resolve({ key: targetKey, category, facilities })
            }
          } else {
            if (allResults.length > 0) {
              const facilities = allResults.map(place => ({
                name: place.place_name,
                distance: calculateDistance(
                  coords.lat, coords.lng,
                  parseFloat(place.y), parseFloat(place.x)
                )
              }))
              facilities.sort((a, b) => a.distance - b.distance)
              resolve({ key: targetKey, category, facilities })
            } else {
              resolve({ key: targetKey, category, facilities: [] })
            }
          }
        }

        places.categorySearch(categoryCode, callback, {
          location: new window.kakao.maps.LatLng(coords.lat, coords.lng),
          radius,
          sort: window.kakao.maps.services.SortBy.DISTANCE
        })
      })
    }

    // 주변 시설 자동 검색 실행
    const searchNearbyFacilities = async () => {
      if (!form.coordinates) {
        facilitySearchError.value = '먼저 주소를 검색해주세요.'
        return
      }

      isSearchingFacilities.value = true
      facilitySearchError.value = ''

      const ensureKakaoLoaded = () => {
        return new Promise((resolve, reject) => {
          if (!window.kakao || !window.kakao.maps) {
            reject(new Error('카카오맵 SDK가 로드되지 않았습니다'))
            return
          }
          if (window.kakao.maps.services && window.kakao.maps.services.Places) {
            resolve()
          } else if (typeof window.kakao.maps.load === 'function') {
            window.kakao.maps.load(() => resolve())
          } else {
            reject(new Error('카카오맵 SDK 로드 함수를 찾을 수 없습니다'))
          }
        })
      }

      try {
        await ensureKakaoLoaded()

        // 1. 주변 편의시설 검색 (이름 + 거리, 3km)
        const convenienceSearches = [
          // 마트 (MT1 대형마트 + 이마트/홈플러스/롯데마트 키워드)
          { type: 'category', code: 'MT1', key: 'mart', category: 'convenience' },
          { type: 'keyword', keyword: '이마트', key: 'mart', category: 'convenience' },
          { type: 'keyword', keyword: '홈플러스', key: 'mart', category: 'convenience' },
          { type: 'keyword', keyword: '롯데마트', key: 'mart', category: 'convenience' },
          // 백화점 (키워드)
          { type: 'keyword', keyword: '롯데백화점', key: 'department', category: 'convenience' },
          { type: 'keyword', keyword: '신세계백화점', key: 'department', category: 'convenience' },
          { type: 'keyword', keyword: '현대백화점', key: 'department', category: 'convenience' },
          { type: 'keyword', keyword: '갤러리아백화점', key: 'department', category: 'convenience' },
          // 종합병원 (HP8)
          { type: 'category', code: 'HP8', key: 'hospital', category: 'convenience' },
          // 공원 (키워드)
          { type: 'keyword', keyword: '공원', key: 'park', category: 'convenience' },
          // 약국 (PM9)
          { type: 'category', code: 'PM9', key: 'pharmacy', category: 'convenience' }
        ]

        // 2. 주변 대중교통 검색 (이름 + 거리, 3km)
        const transportSearches = [
          // 지하철역
          { type: 'category', code: 'SW8', key: 'subway', category: 'transport' },
          // 고속철도/일반철도
          { type: 'keyword', keyword: 'KTX역', key: 'rail', category: 'transport' },
          { type: 'keyword', keyword: 'SRT역', key: 'rail', category: 'transport' },
          { type: 'keyword', keyword: '기차역', key: 'rail', category: 'transport' },
          // 버스정류장
          { type: 'keyword', keyword: '버스정류장', key: 'bus', category: 'transport' }
        ]

        // 3. 주변 학군 검색 (이름 + 거리, 3km)
        const educationSearches = [
          { type: 'category', code: 'PS3', key: 'daycare', category: 'education' },
          { type: 'keyword', keyword: '유치원', key: 'kindergarten', category: 'education' },
          { type: 'keyword', keyword: '초등학교', key: 'elementary', category: 'education' },
          { type: 'keyword', keyword: '중학교', key: 'middle', category: 'education' },
          { type: 'keyword', keyword: '고등학교', key: 'high', category: 'education' }
        ]

        // 병렬로 편의시설 검색
        const convenienceResults = await Promise.all(
          convenienceSearches.map(search => {
            if (search.type === 'category') {
              return searchByCategory(search.code, search.key, search.category, 3000)
            } else {
              return searchByKeyword(search.keyword, search.key, search.category, 3000)
            }
          })
        )

        // 편의시설 결과 병합
        const convenienceMerged = {}
        convenienceResults.forEach(({ key, facilities }) => {
          if (!convenienceMerged[key]) convenienceMerged[key] = []
          convenienceMerged[key].push(...facilities)
        })
        Object.keys(convenienceMerged).forEach(key => {
          const unique = []
          const seen = new Set()
          convenienceMerged[key].forEach(f => {
            if (!seen.has(f.name)) {
              seen.add(f.name)
              unique.push(f)
            }
          })
          unique.sort((a, b) => a.distance - b.distance)
          form.facilities.convenience[key] = unique
        })

        // 병렬로 대중교통 검색
        const transportResults = await Promise.all(
          transportSearches.map(search => {
            if (search.type === 'category') {
              return searchByCategory(search.code, search.key, search.category, 3000)
            } else {
              return searchByKeyword(search.keyword, search.key, search.category, 3000)
            }
          })
        )
        const transportMerged = {}
        transportResults.forEach(({ key, facilities }) => {
          if (!transportMerged[key]) transportMerged[key] = []
          transportMerged[key].push(...facilities)
        })
        Object.keys(transportMerged).forEach(key => {
          const unique = []
          const seen = new Set()
          transportMerged[key].forEach(f => {
            if (!seen.has(f.name)) {
              seen.add(f.name)
              unique.push(f)
            }
          })
          unique.sort((a, b) => a.distance - b.distance)
          form.facilities.transport[key] = unique
        })

        // 병렬로 학군 검색
        const educationResults = await Promise.all(
          educationSearches.map(search => {
            if (search.type === 'category') {
              return searchByCategory(search.code, search.key, search.category, 3000)
            } else {
              return searchByKeyword(search.keyword, search.key, search.category, 3000)
            }
          })
        )
        const educationMerged = {}
        educationResults.forEach(({ key, facilities }) => {
          if (!educationMerged[key]) educationMerged[key] = []
          educationMerged[key].push(...facilities)
        })
        Object.keys(educationMerged).forEach(key => {
          const unique = []
          const seen = new Set()
          educationMerged[key].forEach(f => {
            if (!seen.has(f.name)) {
              seen.add(f.name)
              unique.push(f)
            }
          })
          unique.sort((a, b) => a.distance - b.distance)
          form.facilities.education[key] = unique
        })

      } catch (error) {
        console.error('시설 검색 오류:', error)
        facilitySearchError.value = '시설 검색 중 오류가 발생했습니다.'
      } finally {
        isSearchingFacilities.value = false
      }
    }

    // 시설 삭제
    const removeFacility = (category, key, index) => {
      form.facilities[category][key].splice(index, 1)
    }

    // 시설 수동 추가
    const addFacilityManual = (category, key) => {
      const input = manualInput[category][key]
      if (!input.name.trim()) {
        alert('시설명을 입력해주세요.')
        return
      }
      const distance = parseInt(input.distance) || 0
      form.facilities[category][key].push({
        name: input.name.trim(),
        distance: distance
      })
      // 거리순 정렬
      form.facilities[category][key].sort((a, b) => a.distance - b.distance)
      // 입력 초기화
      input.name = ''
      input.distance = ''
    }

    // coordinates 변경 시 지도 업데이트
    watch(() => form.coordinates, async (newCoords) => {
      if (newCoords) {
        await nextTick()
        initMapPreview()
      }
    })

    // 폼 제출
    const handleSubmit = async () => {
      if (!form.name || !form.address) {
        errorMessage.value = '현장명과 주소는 필수입니다.'
        return
      }

      if (!form.regionMain) {
        errorMessage.value = '광역시/도를 선택해주세요.'
        return
      }

      isSubmitting.value = true
      errorMessage.value = ''

      // types에서 buildings 배열로 변환
      const processedTypes = form.types.map((type) => ({
        name: type.name,
        supplyArea: type.supplyArea,
        pyeong: type.pyeong,
        price: type.price,
        buildings: type.buildingsText
          ? type.buildingsText.split(',').map((b) => b.trim()).filter(Boolean)
          : [],
        imageUrl: type.imageUrl || ''
      }))

      const propertyData = {
        name: form.name,
        address: form.address,
        regionMain: form.regionMain,
        regionSub: form.regionSub,
        propertyType: form.propertyType,
        initialFund: form.initialFund,
        middlePayment: form.middlePayment,
        themes: form.themes,
        status: form.status,
        totalUnits: form.totalUnits,
        totalBuildings: form.totalBuildings,
        maxFloor: form.maxFloor,
        parkingPerUnit: form.parkingPerUnit,
        floorAreaRatio: form.floorAreaRatio,
        buildingCoverageRatio: form.buildingCoverageRatio,
        gasType: form.gasType,
        heatingType: form.heatingType,
        moveInDate: form.moveInDate,
        types: processedTypes,
        facilities: form.facilities,
        coordinates: form.coordinates
      }

      // 타입 이미지 배열 생성
      const typeImages = Object.entries(typeImageFiles.value).map(([index, file]) => ({
        typeIndex: parseInt(index),
        file
      }))

      const result = await createProperty(propertyData, thumbnailFile.value, typeImages)

      isSubmitting.value = false

      if (result.success) {
        router.push('/admin/properties')
      } else {
        errorMessage.value = result.error || '현장 등록에 실패했습니다.'
      }
    }

    return {
      form,
      manualInput,
      openSections,
      sectionList,
      statusOptions,
      regionMainOptions,
      propertyTypeOptions,
      themeOptions,
      facilityLabels,
      isSubmitting,
      errorMessage,
      thumbnailPreview,
      typeImageInputs,
      mapPreviewContainer,
      isSearchingFacilities,
      facilitySearchError,
      toggleSection,
      handleThumbnailChange,
      handleThumbnailDrop,
      removeThumbnail,
      addType,
      removeType,
      triggerTypeImageUpload,
      handleTypeImageChange,
      searchAddress,
      searchNearbyFacilities,
      removeFacility,
      addFacilityManual,
      formatDistance,
      handleSubmit
    }
  }
}
</script>
